import React, { createContext, useContext, useReducer, useCallback, useMemo } from "react";

// Types
export type ServiceType = "project" | "partnership" | "consultation" | "general";
export type StepType = "service" | "details" | "contact" | "success";

export interface ContactData {
  name: string;
  email: string;
  phone: string;
  organization: string;
  website: string;
  referralSource: string;
}

export interface MultiStepFormState {
  currentStep: StepType;
  currentStepIndex: number;
  serviceType: ServiceType | null;
  answers: Record<string, unknown>;
  contact: ContactData;
  isSubmitting: boolean;
  error: string | null;
}

type FormAction =
  | { type: "SELECT_SERVICE"; service: ServiceType }
  | { type: "SET_ANSWER"; questionId: string; value: unknown }
  | { type: "SET_CONTACT"; field: keyof ContactData; value: string }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_SUCCESS" }
  | { type: "SUBMIT_ERROR"; error: string }
  | { type: "RESET" };

const STEPS: StepType[] = ["service", "details", "contact", "success"];

const initialState: MultiStepFormState = {
  currentStep: "service",
  currentStepIndex: 0,
  serviceType: null,
  answers: {},
  contact: {
    name: "",
    email: "",
    phone: "",
    organization: "",
    website: "",
    referralSource: "",
  },
  isSubmitting: false,
  error: null,
};

function formReducer(state: MultiStepFormState, action: FormAction): MultiStepFormState {
  switch (action.type) {
    case "SELECT_SERVICE":
      return {
        ...state,
        serviceType: action.service,
        answers: {}, // Reset answers when changing service type
      };

    case "SET_ANSWER":
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.questionId]: action.value,
        },
      };

    case "SET_CONTACT":
      return {
        ...state,
        contact: {
          ...state.contact,
          [action.field]: action.value,
        },
      };

    case "NEXT_STEP": {
      const nextIndex = Math.min(state.currentStepIndex + 1, STEPS.length - 1);
      return {
        ...state,
        currentStepIndex: nextIndex,
        currentStep: STEPS[nextIndex],
      };
    }

    case "PREV_STEP": {
      const prevIndex = Math.max(state.currentStepIndex - 1, 0);
      return {
        ...state,
        currentStepIndex: prevIndex,
        currentStep: STEPS[prevIndex],
      };
    }

    case "SUBMIT_START":
      return {
        ...state,
        isSubmitting: true,
        error: null,
      };

    case "SUBMIT_SUCCESS":
      return {
        ...state,
        isSubmitting: false,
        currentStep: "success",
        currentStepIndex: STEPS.indexOf("success"),
      };

    case "SUBMIT_ERROR":
      return {
        ...state,
        isSubmitting: false,
        error: action.error,
      };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// Context
interface MultiStepFormContextValue {
  state: MultiStepFormState;
  // Selectors
  currentStep: StepType;
  currentStepIndex: number;
  serviceType: ServiceType | null;
  answers: Record<string, unknown>;
  contact: ContactData;
  isSubmitting: boolean;
  error: string | null;
  isFirstStep: boolean;
  isLastStep: boolean;
  progress: number;
  // Actions
  selectService: (service: ServiceType) => void;
  setAnswer: (questionId: string, value: unknown) => void;
  setContact: (field: keyof ContactData, value: string) => void;
  nextStep: () => void;
  prevStep: () => void;
  submitForm: () => Promise<boolean>;
  reset: () => void;
  canProceed: () => boolean;
}

const MultiStepFormContext = createContext<MultiStepFormContextValue | null>(null);

interface MultiStepFormProviderProps {
  children: React.ReactNode;
  onSubmit: (data: {
    serviceType: ServiceType;
    answers: Record<string, unknown>;
    contact: ContactData;
  }) => Promise<boolean>;
}

export function MultiStepFormProvider({ children, onSubmit }: MultiStepFormProviderProps) {
  const [state, dispatch] = useReducer(formReducer, initialState);

  const selectService = useCallback((service: ServiceType) => {
    dispatch({ type: "SELECT_SERVICE", service });
  }, []);

  const setAnswer = useCallback((questionId: string, value: unknown) => {
    dispatch({ type: "SET_ANSWER", questionId, value });
  }, []);

  const setContact = useCallback((field: keyof ContactData, value: string) => {
    dispatch({ type: "SET_CONTACT", field, value });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: "NEXT_STEP" });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: "PREV_STEP" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const submitForm = useCallback(async (): Promise<boolean> => {
    if (!state.serviceType) return false;

    dispatch({ type: "SUBMIT_START" });

    try {
      const success = await onSubmit({
        serviceType: state.serviceType,
        answers: state.answers,
        contact: state.contact,
      });

      if (success) {
        dispatch({ type: "SUBMIT_SUCCESS" });
        return true;
      } else {
        dispatch({ type: "SUBMIT_ERROR", error: "Submission failed. Please try again." });
        return false;
      }
    } catch (error) {
      dispatch({
        type: "SUBMIT_ERROR",
        error: error instanceof Error ? error.message : "An error occurred",
      });
      return false;
    }
  }, [state.serviceType, state.answers, state.contact, onSubmit]);

  const canProceed = useCallback((): boolean => {
    switch (state.currentStep) {
      case "service":
        return state.serviceType !== null;
      case "details":
        // For general inquiries, just need a message
        if (state.serviceType === "general") {
          return !!state.answers.message && String(state.answers.message).trim().length > 0;
        }
        // For other types, need at least the main question answered
        return Object.keys(state.answers).length > 0;
      case "contact":
        return (
          state.contact.name.trim().length > 0 &&
          state.contact.email.trim().length > 0 &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.contact.email)
        );
      default:
        return true;
    }
  }, [state.currentStep, state.serviceType, state.answers, state.contact]);

  const value = useMemo<MultiStepFormContextValue>(
    () => ({
      state,
      currentStep: state.currentStep,
      currentStepIndex: state.currentStepIndex,
      serviceType: state.serviceType,
      answers: state.answers,
      contact: state.contact,
      isSubmitting: state.isSubmitting,
      error: state.error,
      isFirstStep: state.currentStepIndex === 0,
      isLastStep: state.currentStep === "contact",
      progress: ((state.currentStepIndex + 1) / (STEPS.length - 1)) * 100, // Exclude success step
      selectService,
      setAnswer,
      setContact,
      nextStep,
      prevStep,
      submitForm,
      reset,
      canProceed,
    }),
    [state, selectService, setAnswer, setContact, nextStep, prevStep, submitForm, reset, canProceed]
  );

  return (
    <MultiStepFormContext.Provider value={value}>
      {children}
    </MultiStepFormContext.Provider>
  );
}

export function useMultiStepForm(): MultiStepFormContextValue {
  const context = useContext(MultiStepFormContext);
  if (!context) {
    throw new Error("useMultiStepForm must be used within a MultiStepFormProvider");
  }
  return context;
}
